import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight, Coins, Globe, Landmark, Lightbulb } from "lucide-react";

const advisoryServices = [
  {
    id: "digital-assets",
    title: "Digital Assets",
    subtitle: "Strategic Advisory",
    icon: Coins,
    intro: "Digital assets are rapidly becoming a new layer of the global financial system. From tokenized commodities and real-world assets to decentralized financial infrastructure, digital assets unlock new forms of capital formation, liquidity, and investment.",
    description: "We advise governments, institutions, and enterprises on developing digital asset strategies that align with regulatory frameworks while enabling innovation.",
    services: [
      "Digital asset strategy and policy frameworks",
      "Tokenization of real-world assets and commodities",
      "Institutional adoption of blockchain technologies",
      "Regulatory and governance models for digital asset ecosystems",
      "Digital capital market infrastructure design"
    ],
    outcome: "Our goal is to help institutions harness digital assets as a strategic economic opportunity rather than a regulatory challenge."
  },
  {
    id: "digital-economy",
    title: "Digital Economy",
    subtitle: "Strategic Advisory",
    icon: Globe,
    intro: "The digital economy is becoming the foundation of modern national competitiveness. Countries that successfully build digital infrastructure, innovation ecosystems, and technology-driven industries will lead the next generation of economic growth.",
    description: "We advise governments and institutions on designing national digital economy strategies that integrate technology, finance, and policy.",
    services: [
      "Digital economy policy and strategic frameworks",
      "Digital public infrastructure development",
      "Fintech and startup ecosystem design",
      "Cross-border digital trade and financial innovation",
      "Technology-driven economic transformation initiatives"
    ],
    outcome: "We focus on practical implementation strategies that allow nations and organizations to accelerate growth, innovation, and financial inclusion in the digital era."
  },
  {
    id: "cbdc",
    title: "CBDC Technologies",
    subtitle: "Strategic Advisory",
    icon: Landmark,
    intro: "Central Bank Digital Currencies (CBDCs) represent one of the most important innovations in modern monetary systems. Properly designed CBDCs can strengthen monetary sovereignty, improve payment efficiency, and expand financial inclusion.",
    description: "We provide strategic advisory to governments and central banks on the design, architecture, and deployment of CBDC ecosystems.",
    services: [
      "CBDC strategy and policy design",
      "Technology architecture and infrastructure planning",
      "Integration with national payment systems",
      "Cross-border CBDC interoperability models",
      "Regulatory and security frameworks"
    ],
    outcome: "Our approach focuses on building future-ready digital currency systems that strengthen national financial infrastructure while enabling participation in the emerging global digital financial network."
  },
  {
    id: "approach",
    title: "Our Approach",
    subtitle: "Advisory Framework",
    icon: Lightbulb,
    intro: "We combine expertise in financial systems, digital technologies, and economic strategy to support governments and institutions in navigating complex digital transformation initiatives.",
    description: "Our advisory approach focuses on delivering strategic clarity and practical implementation across all engagements.",
    services: [
      "Strategic clarity and policy alignment",
      "Technology architecture and system design",
      "Institutional capacity building",
      "Scalable and secure implementation frameworks"
    ],
    outcome: "By bridging technology innovation with economic strategy, we help institutions build digital financial systems that will define the next era of global economic development."
  }
];

const AdvisoryPage = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? advisoryServices.length - 1 : prev - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === advisoryServices.length - 1 ? 0 : prev + 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDotClick = (index) => {
    setCurrentIndex(index);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const currentService = advisoryServices[currentIndex];
  const Icon = currentService.icon;

  return (
    <div data-testid="advisory-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-black min-h-[100dvh] flex items-center">
        <div className="max-w-[1200px] mx-auto px-6 w-full">
          {/* Desktop Layout */}
          <div className="hidden md:flex items-center gap-8">
            {/* Left Arrow */}
            <button
              onClick={goToPrevious}
              className="flex-shrink-0 w-14 h-14 rounded-full bg-[#1C1C1E] border border-white/10 flex items-center justify-center text-[#86868B] hover:text-[#F5F5F7] hover:border-[#2997FF]/50 transition-all"
              data-testid="advisory-prev"
              aria-label="Previous service"
            >
              <ChevronLeft size={28} />
            </button>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentIndex}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                  transition={{ duration: 0.3 }}
                  className="text-center"
                >
                  {/* Icon */}
                  <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-[#2997FF]/10 flex items-center justify-center">
                    <Icon size={40} className="text-[#2997FF]" />
                  </div>

                  {/* Subtitle */}
                  <p className="text-[#2997FF] text-sm font-medium tracking-wide uppercase mb-3">
                    {currentService.subtitle}
                  </p>

                  {/* Title */}
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-[#F5F5F7] tracking-tight mb-8" data-testid="advisory-title">
                    {currentService.title}
                  </h1>

                  {/* Intro */}
                  <p className="text-lg md:text-xl text-[#86868B] leading-relaxed mb-8 max-w-3xl mx-auto">
                    {currentService.intro}
                  </p>

                  {/* Description */}
                  <p className="text-base text-[#F5F5F7]/80 leading-relaxed mb-8 max-w-3xl mx-auto">
                    {currentService.description}
                  </p>

                  {/* Services List */}
                  <div className="max-w-2xl mx-auto mb-8">
                    <div className="grid gap-3">
                      {currentService.services.map((service, index) => (
                        <div key={index} className="flex items-center gap-3 text-left">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#2997FF] flex-shrink-0" />
                          <span className="text-[#86868B]">{service}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Outcome */}
                  <p className="text-base text-[#F5F5F7]/90 leading-relaxed max-w-3xl mx-auto italic">
                    {currentService.outcome}
                  </p>
                </motion.div>
              </AnimatePresence>

              {/* Dots Indicator */}
              <div className="flex items-center justify-center gap-2 mt-12">
                {advisoryServices.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => handleDotClick(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentIndex ? "bg-[#2997FF] w-6" : "bg-[#86868B]/50 hover:bg-[#86868B]"
                    }`}
                    aria-label={`Go to ${advisoryServices[index].title}`}
                  />
                ))}
              </div>
            </div>

            {/* Right Arrow */}
            <button
              onClick={goToNext}
              className="flex-shrink-0 w-14 h-14 rounded-full bg-[#1C1C1E] border border-white/10 flex items-center justify-center text-[#86868B] hover:text-[#F5F5F7] hover:border-[#2997FF]/50 transition-all"
              data-testid="advisory-next"
              aria-label="Next service"
            >
              <ChevronRight size={28} />
            </button>
          </div>

          {/* Mobile Layout */}
          <div className="md:hidden">
            {/* Content */}
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="text-center"
              >
                {/* Icon */}
                <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-[#2997FF]/10 flex items-center justify-center">
                  <Icon size={28} className="text-[#2997FF]" />
                </div>

                {/* Subtitle */}
                <p className="text-[#2997FF] text-xs font-medium tracking-wide uppercase mb-2">
                  {currentService.subtitle}
                </p>

                {/* Title */}
                <h1 className="text-2xl font-semibold text-[#F5F5F7] tracking-tight mb-6">
                  {currentService.title}
                </h1>

                {/* Intro */}
                <p className="text-sm text-[#86868B] leading-relaxed mb-6">
                  {currentService.intro}
                </p>

                {/* Description */}
                <p className="text-sm text-[#F5F5F7]/80 leading-relaxed mb-6">
                  {currentService.description}
                </p>

                {/* Services List */}
                <div className="mb-6">
                  <div className="grid gap-2">
                    {currentService.services.map((service, index) => (
                      <div key={index} className="flex items-start gap-2 text-left">
                        <div className="w-1 h-1 rounded-full bg-[#2997FF] flex-shrink-0 mt-2" />
                        <span className="text-sm text-[#86868B]">{service}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Outcome */}
                <p className="text-sm text-[#F5F5F7]/90 leading-relaxed italic">
                  {currentService.outcome}
                </p>
              </motion.div>
            </AnimatePresence>

            {/* Navigation */}
            <div className="flex items-center justify-center gap-6 mt-10">
              <button
                onClick={goToPrevious}
                className="w-12 h-12 rounded-full bg-[#1C1C1E] border border-white/10 flex items-center justify-center text-[#86868B] active:text-[#F5F5F7] active:border-[#2997FF]/50 transition-all"
                aria-label="Previous service"
              >
                <ChevronLeft size={24} />
              </button>

              {/* Dots */}
              <div className="flex items-center gap-2">
                {advisoryServices.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => handleDotClick(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentIndex ? "bg-[#2997FF] w-4" : "bg-[#86868B]/50"
                    }`}
                    aria-label={`Go to ${advisoryServices[index].title}`}
                  />
                ))}
              </div>

              <button
                onClick={goToNext}
                className="w-12 h-12 rounded-full bg-[#1C1C1E] border border-white/10 flex items-center justify-center text-[#86868B] active:text-[#F5F5F7] active:border-[#2997FF]/50 transition-all"
                aria-label="Next service"
              >
                <ChevronRight size={24} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#1C1C1E]">
        <div className="max-w-[1200px] mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-5xl font-semibold text-[#F5F5F7] tracking-tight mb-4">
              Ready to discuss your strategy?
            </h2>
            <p className="text-lg text-[#86868B] max-w-2xl mx-auto mb-8">
              Let's explore how our strategic advisory services can help your organization navigate the digital transformation landscape.
            </p>
            <Link
              to="/contact"
              className="btn-primary px-8 py-4 rounded-full text-base font-medium inline-flex items-center gap-2"
              data-testid="advisory-cta"
            >
              Get in Touch
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AdvisoryPage;
